import axios from 'axios';
import { FC } from 'react';
import { FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AsyncSelect from 'react-select/async';
import { SText } from 'src/components/SText';
import { FormsErrorParser } from '../errors';

export type Address = {
  label: string;
  geometry: { lat: number; lon: number };
  city: string;
  zipCode: string;
  country: string;
};

type Props = {
  error?: FieldError | undefined;
  extraClass?: string;
  inputClass?: string;
  onAddressSelect?: (address: Address) => void;
};

export const AddressAutocomplete: FC<Props> = ({ error, extraClass, inputClass, onAddressSelect }) => {
  const { t } = useTranslation();

  // Fetch suggestions from API
  const fetchSuggestions = async (inputValue: string): Promise<Address[]> => {
    if (inputValue.length < 3) {
      return []; // No results if the input is too short
    }

    try {
      const response = await axios.get<{ features: any[] }>(`https://api-adresse.data.gouv.fr/search/`, {
        params: {
          q: inputValue,
          limit: 5,
        },
      });

      return response.data.features.map((feature: any) => ({
        label: feature.properties.label,
        geometry: {
          lat: feature.geometry.coordinates[1],
          lon: feature.geometry.coordinates[0],
        },
        city: feature.properties.city || feature.properties.locality || '',
        zipCode: feature.properties.postcode || '',
        country: 'France', // API provides French addresses
      }));
    } catch (error) {
      console.error('Error fetching address data:', error);
      return [];
    }
  };

  // Handle address selection
  const handleChange = (selectedOption: Address | null) => {
    if (selectedOption && onAddressSelect) {
      onAddressSelect(selectedOption);
    }
  };

  const interactiveClasses = error ? 'border-red-500' : 'border-gray-400';

  return (
    <div className="relative mt-4">
      <SText
        classNames="block text-md text-primary font-semibold my-1 text-left flex w-full"
        label={t('forms.address')}
      />
      <div
        className={`flex flex-row space-x-1 rounded-md my-2 px-2 items-end md:shadow-xs ${interactiveClasses} border py-2  text-gray-500 focus-within:text-gray-600 md:shadow-sc drop-shadow-sc ${extraClass} w-full`}
      >
        <AsyncSelect
          cacheOptions
          loadOptions={fetchSuggestions}
          defaultOptions
          placeholder={t('client-delivery-requests.your-address')}
          onChange={handleChange}
          noOptionsMessage={() => t('client-delivery-requests.no-address-found')}
          className={`w-full ${inputClass}`}
          styles={{
            control: (provided) => ({
              ...provided,
              border: error ? '1px solid #F56565' : '1px solid #CBD5E0',
              boxShadow: error ? '0 0 0 1px #F56565' : 'none',
            }),
          }}
        />
      </div>
      <FormsErrorParser error={error} />
    </div>
  );
};
